"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
function ResourceLibraryConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-32 rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-8">
        <p className="text-muted-foreground text-sm mb-4">
          Failed to load resources.
        </p>
        <Button onClick={refetch} variant="outline" size="sm">
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-center text-muted-foreground py-8">
        No resources available at this time.
      </p>;
  }
  return <div className="flex flex-col gap-4">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5 hover:scale-[1.02] bg-light-background border border-border">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center p-4">
            {post.featuredImage && <div className="w-full md:w-32 h-32 flex-shrink-0 overflow-hidden rounded-md">
                <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:scale-105 group-hover:brightness-105" />
              </div>}

            <div className="flex-1 min-w-0">
              <CardHeader className="p-0">
                <CardTitle className="text-lg md:text-xl text-foreground-light mb-2">
                  {post.title}
                </CardTitle>
                {post.excerpt && <CardDescription className="text-sm text-muted-foreground line-clamp-2">
                    {post.excerpt.replace(/<[^>]*>/g, "")}
                  </CardDescription>}
              </CardHeader>
            </div>

            {post.link && <div className="flex-shrink-0 self-center md:self-auto">
                <Link to={post.link}>
                  <Button variant="outline" size="sm" className="group/btn transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:translate-x-1">
                    Read more
                    <ArrowRight className="ml-1 size-4 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)] group-hover/btn:translate-x-0.5" />
                  </Button>
                </Link>
              </div>}
          </div>
        </Card>)}
    </div>;
}
export default function ResourceLibrary() {
  return <section className="w-full py-12 md:py-16 lg:py-20 bg-light-background">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-light mb-3">
            Resource Library
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl">
            Explore our collection of educational resources, articles, and insights for special education.
          </p>
        </div>

        <PostsProvider className="animate-fade-in-up" wp_query={{
        per_page: 5,
        order: "desc",
        orderby: "date"
      }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "_links.wp:featuredmedia"]}>
          <ResourceLibraryConsumer />
        </PostsProvider>
      </div>
    </section>;
}